import React, { useState, useEffect } from 'react';
import ReactPaginate from 'react-paginate';
import { Col, Row } from 'react-bootstrap';
import { FaFilePdf } from 'react-icons/fa';
import { handleBackgroundCheckList, updateBackgroundCheckStatus, showToast, usTimezone } from '../../Utils/utils';
import Loaderspinner from '../../../components/Loader';
import ConfirmationModal from '../../../components/Modal/ConfirmationModal';

const STATUS_LABEL = {
  NOT_VERIFIED: 'Not Verified',
  PENDING: 'Pending',
  VERIFIED: 'Verified',
  REJECTED: 'Rejected',
  EXPIRED: 'Expired',
};

const STATUS_CLASS = {
  NOT_VERIFIED: 'unverifiedstatus',
  PENDING: 'pendingstatus',
  VERIFIED: 'verifiedstatus',
  REJECTED: 'unverifiedstatus',
  EXPIRED: 'unverifiedstatus',
};

const BackgroundCheckList = () => {
  const [statusFilter, setStatusFilter] = useState("");
  const [list, setList] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [confirmTarget, setConfirmTarget] = useState(null); // { id, action }

  useEffect(() => {
    if (search.length === 0 || search.length >= 3) {
      fetchList();
    }
  }, [statusFilter, page, search]);

  const fetchList = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const limit = 10;
      const offset = page * limit;
      const response = await handleBackgroundCheckList({
        status: statusFilter,
        limit,
        offset,
        token,
        search,
      });
      setList(response?.data?.rows || []);
      setTotalPages(response.data.count / limit);
    } catch (error) {
      console.error("Error fetching background check list:", error);
    } finally {
      setLoading(false);
    }
  };

  const onConfirmAction = async () => {
    if (!confirmTarget) return;
    try {
      setLoading(true);
      const res = await updateBackgroundCheckStatus(confirmTarget.id, confirmTarget.action);
      if (res?.status === 200) {
        showToast(res.message, "success");
        fetchList();
      } else {
        showToast(res?.message, "error");
      }
    } catch (error) {
      showToast(error?.message, "error");
    } finally {
      setLoading(false);
      setConfirmTarget(null);
    }
  };

  return (
    <>
      {loading && <Loaderspinner />}
      <div className="container-fluid mt--6">
        <div className="row">
          <div className="col">
            <div className="card">
              <div className="card-header border-0">
                <div className="d-flex flex-wrap justify-content-between align-items-center">
                  <div className='Table-title row'>
                    <h5 className="mb-0 w-auto">Background Checks</h5>
                  </div>
                  <div className="mt-3 mt-lg-0">
                    <input
                      type="text"
                      className="search-input mx-2"
                      placeholder="Search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    <select
                      className="custom-select-dropdown mx-2"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="" className='option'>All Statuses</option>
                      <option value="NOT_VERIFIED" className='option'>Not Verified</option>
                      <option value="PENDING" className='option'>Pending</option>
                      <option value="VERIFIED" className='option'>Verified</option>
                      <option value="REJECTED" className='option'>Rejected</option>
                      <option value="EXPIRED" className='option'>Expired</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="table-responsive">
                <table className="table align-items-center table-flush">
                  <thead className="thead-light">
                    <tr>
                      <th>Action</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone number</th>
                      <th>Report</th>
                      <th>Start Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.length > 0 ? (
                      list.map((row, index) => {
                        const about = row.about || {};
                        const status = about.background_check_status || 'NOT_VERIFIED';
                        return (
                          <tr key={index}>
                            <td>
                              <div style={{ width: "160px" }}>
                                {status === 'PENDING' && (
                                  <>
                                    <button className="viewproject-btn pull-right mb-1" onClick={() => setConfirmTarget({ id: row.id, action: 'approve' })}>
                                      <span className="text-white">Approve</span>
                                    </button>
                                    <button className="viewproject-btn-2 pull-right mb-1" onClick={() => setConfirmTarget({ id: row.id, action: 'reject' })}>
                                      <span className="text-white">Reject</span>
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                            <td><div style={{ width: "130px" }}>{about.name} {about.lastname}</div></td>
                            <td><div style={{ width: "160px" }}>{about.email}</div></td>
                            <td><div style={{ width: "130px" }}>{about.phone}</div></td>
                            <td>
                              <div style={{ width: "110px" }}>
                                {about.background_check_pdf_url ? (
                                  <a href={about.background_check_pdf_url} target="_blank" rel="noreferrer">
                                    <FaFilePdf /> View
                                  </a>
                                ) : '—'}
                              </div>
                            </td>
                            <td>
                              <div style={{ width: "130px" }}>
                                {about.background_check_start_date ? usTimezone(about.background_check_start_date) : '—'}
                              </div>
                            </td>
                            <td>
                              <div style={{ width: "120px" }} className={`p-1 ${STATUS_CLASS[status]}`}>
                                <span>{STATUS_LABEL[status] || status}</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center"><b>No records found</b></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <Row>
              <Col xs={12}>
                {list.length > 0 && (
                  <div className='paginate-css'>
                    <ReactPaginate
                      previousLabel="<"
                      nextLabel=">"
                      breakLabel="..."
                      pageCount={totalPages}
                      marginPagesDisplayed={1}
                      pageRangeDisplayed={2}
                      onPageChange={({ selected }) => setPage(selected)}
                      forcePage={page}
                      containerClassName="pagination"
                      activeClassName="active"
                      previousClassName={page === 0 ? 'disabled' : ''}
                      nextClassName={totalPages === page + 1 ? 'disabled' : ''}
                    />
                  </div>
                )}
              </Col>
            </Row>
          </div>
        </div>
      </div>
      <ConfirmationModal
        show={!!confirmTarget}
        onClose={() => setConfirmTarget(null)}
        onConfirm={onConfirmAction}
        title={confirmTarget?.action === 'approve' ? 'Approve Background Check' : 'Reject Background Check'}
        body={`Are you sure you want to ${confirmTarget?.action} this background check?`}
        confirmText={confirmTarget?.action === 'approve' ? 'Approve' : 'Reject'}
      />
    </>
  );
};

export default BackgroundCheckList;
